import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Collapse,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Cancel01Icon, ChevronDownIcon, ChevronRightIcon, Search01Icon} from "@hugeicons/core-free-icons";
import {RAIL, rgba} from "../rail/railTokens";
import {SECTION_STYLES, SideSectionContext, useSideMenuStyle} from "../sideMenuStyle";

const PAGE = 5;

/**
 * A collapsible, searchable, paginated section of the flow side menu.
 * Purely presentational – parents pass the resolved `items`, a `mapItem`
 * renderer, and the search value + setter.
 */
export const ListBlock = (props) => {
    const {variant} = useSideMenuStyle();
    return variant === "panel" ? <PanelListBlock {...props}/> : <RailListBlock {...props}/>;
};

// Registry projects: a section of the white list panel. The shared panel search drives every section search.
const PanelListBlock = ({items = [], title, mapItem, onChangeSearch, onCreate, isOpen = false}) => {
    const {search: sharedSearch} = useSideMenuStyle();
    const [open, setOpen] = useState(!!isOpen);
    const [visible, setVisible] = useState(PAGE);
    const section = SECTION_STYLES[title] || {icon: null, color: "#8491F7"};
    const searching = Boolean(sharedSearch?.trim());

    useEffect(() => {
        onChangeSearch?.(sharedSearch || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sharedSearch]);

    const count = items?.length || 0;
    // while searching, every section with a match is shown expanded and the others are hidden
    const expanded = searching ? count > 0 : open;
    const shown = items?.slice(0, searching ? count : visible) || [];

    if (searching && count === 0) return null;

    return (
        <Box sx={{pb: 0.5}}>
            <Stack
                direction="row"
                alignItems="center"
                onClick={() => setOpen(!open)}
                sx={{
                    position: "sticky", top: 0, zIndex: 1, bgcolor: "#fff",
                    px: 1.5, pt: 1.25, pb: 0.75, cursor: "pointer",
                    "&:hover .panel-section-title": {color: "#4A4A63"},
                }}
            >
                <HugeiconsIcon icon={ChevronRightIcon} size={13} color={"#B4B4C3"}
                               style={{transform: expanded ? "rotate(90deg)" : "none", transition: "transform .15s ease", flexShrink: 0}}/>
                <Typography className="panel-section-title" sx={{
                    ml: 0.75, flex: 1, fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase",
                    color: "#8A8AA0", transition: "color .12s ease",
                }}>
                    {title}
                </Typography>
                <Typography sx={{mr: 0.5, fontSize: 11, color: "#B4B4C3"}}>{count}</Typography>
                <Tooltip title={`Add ${title?.toLowerCase()}`} disableInteractive>
                    <IconButton
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            onCreate?.();
                        }}
                        sx={{p: 0.375, borderRadius: "8px !important", color: "#8A8AA0", "&:hover": {bgcolor: "rgba(132, 145, 247, 0.12)", color: "#4B55C9"}}}
                    >
                        <HugeiconsIcon icon={Add01Icon} size={14}/>
                    </IconButton>
                </Tooltip>
            </Stack>

            <Collapse in={expanded} unmountOnExit>
                <SideSectionContext.Provider value={section}>
                    <Box sx={{px: 1}}>
                        {shown.map((item) => mapItem(item))}
                        {count === 0 && (
                            <Typography sx={{px: 1.5, py: 1, fontSize: 12, color: "#B4B4C3"}}>No {title?.toLowerCase()} yet</Typography>
                        )}
                        {!searching && count > PAGE && (
                            <Button
                                fullWidth
                                disableRipple
                                onClick={() => setVisible(visible >= count ? PAGE : count)}
                                sx={{
                                    justifyContent: "flex-start", px: 1.5, py: 0.5, fontSize: 12, fontWeight: 600,
                                    textTransform: "none", color: "#4B55C9", borderRadius: "10px",
                                    "&:hover": {bgcolor: "rgba(132, 145, 247, 0.08)"},
                                }}
                            >
                                {visible >= count ? "Show less" : `Show ${count - PAGE} more`}
                            </Button>
                        )}
                    </Box>
                </SideSectionContext.Provider>
            </Collapse>
        </Box>
    );
};

const RailListBlock = ({items = [], title, mapItem, search, onChangeSearch, onCreate, isOpen = false}) => {
    const [open, setOpen] = useState(!!isOpen);
    const [visible, setVisible] = useState(PAGE);

    useEffect(() => {
        if (!open) setVisible(PAGE);
    }, [open]);

    const count = items?.length || 0;
    const shown = items?.slice(0, visible) || [];

    return (
        <Box
            sx={{
                px: 0.5,
                py: 0.25,
                borderBottom: `1px solid ${rgba(RAIL.accent, 0.1)}`,
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                onClick={() => setOpen(!open)}
                sx={{
                    px: 0.75,
                    py: 0.5,
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: open ? RAIL.ink : RAIL.muted,
                    transition: "background-color .12s ease, color .12s ease",
                    "&:hover": {backgroundColor: rgba(RAIL.accent, 0.06), color: RAIL.ink},
                    "&:hover .list-block-add": {opacity: 1},
                }}
            >
                <HugeiconsIcon icon={ChevronRightIcon}
                    size={14}
                    style={{transform: open ? "rotate(90deg)" : "none", transition: "transform .15s ease", flexShrink: 0}}
                />
                <Typography
                    sx={{
                        ml: 0.5,
                        flex: 1,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: "inherit",
                    }}
                >
                    {title}
                </Typography>
                {count > 0 && (
                    <Box
                        sx={{
                            mr: 0.5,
                            px: 0.6,
                            minWidth: 16,
                            textAlign: "center",
                            borderRadius: "999px",
                            fontSize: 10,
                            fontWeight: 600,
                            lineHeight: "16px",
                            color: RAIL.muted,
                            backgroundColor: rgba(RAIL.accent, 0.1),
                        }}
                    >
                        {count}
                    </Box>
                )}
                <Tooltip title={`Add ${title}`} placement="right" disableInteractive>
                    <IconButton
                        className="list-block-add"
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            onCreate?.();
                        }}
                        sx={{
                            p: 0.25,
                            color: RAIL.muted,
                            opacity: open ? 1 : 0,
                            transition: "opacity .12s ease, background-color .12s ease, color .12s ease",
                            "&:hover": {backgroundColor: rgba(RAIL.accent, 0.12), color: RAIL.accent},
                        }}
                    >
                        <HugeiconsIcon icon={Add01Icon} size={14}/>
                    </IconButton>
                </Tooltip>
            </Stack>

            <Collapse in={open} unmountOnExit>
                <Box sx={{px: 0.75, pt: 0.5, pb: 0.25}}>
                    <TextField
                        size="small"
                        fullWidth
                        value={search}
                        placeholder="Search"
                        onChange={(event) => onChangeSearch(event.target.value)}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontSize: 12.5,
                                backgroundColor: rgba(RAIL.accent, 0.04),
                                "& fieldset": {borderColor: rgba(RAIL.accent, 0.16)},
                                "&:hover fieldset": {borderColor: rgba(RAIL.accent, 0.3)},
                                "&.Mui-focused fieldset": {borderColor: RAIL.accent, borderWidth: "1px"},
                            },
                            "& .MuiOutlinedInput-input": {py: 0.65},
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <HugeiconsIcon icon={Search01Icon} size={14}/>
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" sx={{p: 0.25}} onClick={() => onChangeSearch("")}>
                                        <HugeiconsIcon icon={Cancel01Icon} size={13}/>
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </Box>

                <Box sx={{px: 0.5, pb: 0.5}}>
                    {shown.map((item) => mapItem(item))}

                    {count === 0 && (
                        <Typography sx={{px: 1, py: 0.75, fontSize: 11.5, color: rgba(RAIL.muted, 0.8)}}>
                            {search ? "No matches" : `No ${title?.toLowerCase()} yet`}
                        </Typography>
                    )}

                    {count > PAGE && (
                        <Button
                            fullWidth
                            disableRipple
                            onClick={() => setVisible(visible >= count ? PAGE : count)}
                            startIcon={visible >= count ? <HugeiconsIcon icon={ChevronDownIcon} size={13} style={{transform: "rotate(180deg)"}}/> : <HugeiconsIcon icon={ChevronDownIcon} size={13}/>}
                            sx={{
                                mt: 0.25,
                                fontSize: 11.5,
                                fontWeight: 500,
                                textTransform: "none",
                                color: RAIL.muted,
                                borderRadius: "8px",
                                "&:hover": {backgroundColor: rgba(RAIL.accent, 0.06), color: RAIL.accent},
                            }}
                        >
                            {visible >= count ? "Show less" : `Show ${count - PAGE} more`}
                        </Button>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};
